//
// Copyright (c) 2011-2021 by Curalate, Inc.
//
import { createSelector } from 'reselect';

import { INSIGHTS_ROUTES } from '@insights/insightsSidebarConstants';
import { getCurrentInsightsPageRoute } from '@insights/redux/selectors/getCurrentInsightsPage';
import { ICuralateStore } from '@models/AppState';
import { EcommerceProvider } from '@models/MetricsGatewayApiModels';

export const ADOBE_ECOM_STATS_VALID_START_DATE = new Date('2020-05-01');
const PIXEL_TRIBUTARY_ECOM_DEPRECATION_DATE = new Date('2021-09-22');
export const UMS_VALID_START_DATE = new Date('2018-01-01');
export const GOOGLE_TRACKING_PARAM_ID = 'google';

/**
 * There are three ecommerce connection states that all connections can be mapped to:
 * 1. Full:
 *    This state represents a client with Adobe or a fully enabled Google Analytics account
 *    (i.e. GA with `ecommerceTrackingEnabled` and tracking params set up) connected.
 *    Clients in this state are able to see all on-site and ecom metrics at an account level.
 *
 * 2. CuralatePixel:
 *    This state represents a client with CuralatePixel enabled as their ecom provider.
 *    Curalate Pixel is not able to surface on-site metrics such as visits, but is able to
 *    surface ecom stats such as transactions and revenue.
 *
 * 3. None
 *    This state represents a client with no ecom provider connected (or a GA account without
 *    `ecommerceTrackingEnabled` and/or tracking parameters). Clients in this state can still
 *    have some on-site metrics, but will not receive ecom metrics such as transactions/revenue.
 */
export enum EcomConnectionState {
  Full = 'Full',
  CuralatePixel = 'CuralatePixel',
  None = 'None',
}

/**
 * When checking for ecom stat eligibility, we will also compare the selected date range
 * against a number of criteria to determine if we can only surface partial data for the given
 * provider.
 */
export interface IInsightsEcommerceProviderState {
  ecomConnectionState: EcomConnectionState;
  isPartialData: boolean;
}

const isBeforeValidUMSStartDate = (startDate: Date) => {
  // We do not have valid stats from before UMS existed for any page
  // This will result in potentially inaccurate rate calculation and totals
  return startDate < UMS_VALID_START_DATE;
};

export const getEcomProvider = createSelector(
  (state: ICuralateStore) => state.insights.metadata,
  (metadata) => metadata.ecommerceProvider,
);

export const getHasEcomEnabled = createSelector(
  getEcomProvider,
  (state: ICuralateStore) => state.insights.metadata.ecommerceTrackingEnabled,
  (ecomProvider, isEcommerceTrackingEnabled) => {
    switch (ecomProvider) {
      case EcommerceProvider.GoogleAnalytics:
        return isEcommerceTrackingEnabled;
      case EcommerceProvider.AdobeOmniture:
      case EcommerceProvider.BvPixel:
      case EcommerceProvider.CuralatePixel:
      case EcommerceProvider.Demo:
        return true;
      case EcommerceProvider.NotApplicable:
      default:
        return false;
    }
  },
);

export const getHasReservedGAParam = createSelector(
  (state: ICuralateStore) =>
    state.insights.metadata.configuredTrackingParamGroups,
  (paramGroups) => {
    return (paramGroups ?? []).some((paramGroup) => {
      return (
        paramGroup.id === GOOGLE_TRACKING_PARAM_ID &&
        paramGroup.inUse &&
        paramGroup.params.some((param) => param.curalateReserved)
      );
    });
  },
);

export const getInsightsEcomConnection = createSelector(
  getEcomProvider,
  getHasEcomEnabled,
  getCurrentInsightsPageRoute,
  getHasReservedGAParam,
  (state: ICuralateStore) => state.insights.metadata.dateRange,
  (
    ecomProvider,
    isEcomEnabled,
    currentPage,
    hasResrvedGAParam,
    dateRange,
  ): IInsightsEcommerceProviderState => {
    if (!isEcomEnabled) {
      return {
        ecomConnectionState: EcomConnectionState.None,
        isPartialData: isBeforeValidUMSStartDate(dateRange.startDate),
      };
    }

    if (ecomProvider === EcommerceProvider.AdobeOmniture) {
      // For Adobe clients, ecom stats are not available for L2B, Reveal, and Showroom prior to 2020-05-01
      // Include the Summary page since it contains data from other date restricted pages
      const isDateRestictedPage =
        currentPage === INSIGHTS_ROUTES.SUMMARY ||
        currentPage === INSIGHTS_ROUTES.LIKE2BUY ||
        currentPage === INSIGHTS_ROUTES.REVEAL ||
        currentPage === INSIGHTS_ROUTES.SHOWROOM;
      const containsPartialAdobeStats =
        isDateRestictedPage &&
        dateRange.startDate < ADOBE_ECOM_STATS_VALID_START_DATE;

      const isPartialData =
        containsPartialAdobeStats ||
        isBeforeValidUMSStartDate(dateRange.startDate);

      return {
        ecomConnectionState: EcomConnectionState.Full,
        isPartialData,
      };
    }

    if (ecomProvider === EcommerceProvider.GoogleAnalytics) {
      // If the client does not have reserved GA parameters set up, then we can only
      // surface ecom metrics on the galleries pages.
      // Include the Summary page since it contains data from other date restricted pages
      const requiresReservedParam =
        currentPage === INSIGHTS_ROUTES.LIKE2BUY ||
        currentPage === INSIGHTS_ROUTES.LIKE2BUY_V2 ||
        currentPage === INSIGHTS_ROUTES.LIKE2BUY_SUMMARY ||
        currentPage === INSIGHTS_ROUTES.REVEAL ||
        currentPage === INSIGHTS_ROUTES.SHOWROOM;
      const ecomConnectionState =
        hasResrvedGAParam || !requiresReservedParam
          ? EcomConnectionState.Full
          : EcomConnectionState.None;

      return {
        ecomConnectionState,
        isPartialData: isBeforeValidUMSStartDate(dateRange.startDate),
      };
    }

    if (ecomProvider === EcommerceProvider.Demo) {
      return {
        ecomConnectionState: EcomConnectionState.Full,
        isPartialData: false,
      };
    }

    if (ecomProvider === EcommerceProvider.BvPixel) {
      return {
        ecomConnectionState: EcomConnectionState.Full,
        // Setting to true bc this depends on when the client began capturing galleries data using BV Pixel.
        // TODO PD-298092: Add messaging to consumers of this selector similar to what is done in TouchpointBreakdownTable
        isPartialData: true,
      };
    }

    if (ecomProvider === EcommerceProvider.CuralatePixel) {
      // For Curalate Pixel clients, ecom stats are not available for L2B and Showroom after 2021-09-22
      // This corresponds with the release of the Tributary 3rd party `cuid` cookie deprecation
      // which we relied on to calculate transactions and revenue
      const isDateRestrictedPage =
        currentPage === INSIGHTS_ROUTES.LIKE2BUY ||
        currentPage === INSIGHTS_ROUTES.SHOWROOM;
      const isAfterTributaryPixelEcomDeprecation =
        dateRange.startDate > PIXEL_TRIBUTARY_ECOM_DEPRECATION_DATE;
      const isTributaryDisabled =
        isDateRestrictedPage && isAfterTributaryPixelEcomDeprecation;
      const isUnsupportedTouchpoint =
        currentPage === INSIGHTS_ROUTES.LIKE2BUY_SUMMARY ||
        currentPage === INSIGHTS_ROUTES.LIKE2BUY_V2;
      const ecomConnectionState =
        isTributaryDisabled || isUnsupportedTouchpoint
          ? EcomConnectionState.None
          : EcomConnectionState.CuralatePixel;

      return {
        ecomConnectionState: ecomConnectionState,
        isPartialData: isBeforeValidUMSStartDate(dateRange.startDate),
      };
    }

    return {
      ecomConnectionState: EcomConnectionState.None,
      isPartialData: false,
    };
  },
);

export default getInsightsEcomConnection;
